import { db } from '@/db/client';
import { getPendingUploads } from '@/lib/images/queries';
import { getSupabase } from '@/lib/supabase';
import type { UserId } from '@/schemas/ids';
import { encodeImageToRow } from '../codec';
import { getUserSyncState, setUserLastUploadedAt } from '../state';

export interface UploadStats {
  uploaded: number;
}

/**
 * Upload pending images to Supabase Storage and update metadata
 */
export async function uploadPendingImages(userId: UserId): Promise<UploadStats> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync images');
  }

  const { lastUploadedAt = 0 } = await getUserSyncState(userId, 'images');
  const pending = await getPendingUploads();

  // Filter to only images created after lastUploadedAt and owned by this user
  const imagesToUpload = pending.filter(
    (img) => img.createdAt > lastUploadedAt && img.userId === userId,
  );

  if (imagesToUpload.length === 0) return { uploaded: 0 };

  let uploadedCount = 0;
  let maxCreatedAt = lastUploadedAt;

  // Upload images one by one (storage uploads can be large)
  for (const image of imagesToUpload) {
    try {
      // Update status to uploading
      await db.images.update(image.id, { uploadStatus: 'uploading' });

      // Generate storage paths
      const storagePath = `${userId}/${image.id}/original.webp`;
      const thumbnailPath = `${userId}/${image.id}/thumbnail.webp`;

      // Upload original image to storage
      const { error: uploadError } = await client.storage
        .from('images')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Blob is compatible with FileBody (File | Blob | ArrayBuffer)
        .upload(storagePath, image.localBlob, {
          contentType: 'image/webp',
          upsert: true,
        });

      if (uploadError) {
        throw new Error(`Failed to upload image: ${uploadError.message}`);
      }

      // Upload thumbnail to storage
      const { error: thumbnailError } = await client.storage
        .from('images')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- Blob is compatible with FileBody (File | Blob | ArrayBuffer)
        .upload(thumbnailPath, image.localThumbnail, {
          contentType: 'image/webp',
          upsert: true,
        });

      if (thumbnailError) {
        throw new Error(`Failed to upload thumbnail: ${thumbnailError.message}`);
      }

      // Get public URLs
      const {
        data: { publicUrl },
      } = client.storage.from('images').getPublicUrl(storagePath);
      const {
        data: { publicUrl: thumbnailPublicUrl },
      } = client.storage.from('images').getPublicUrl(thumbnailPath);

      // Insert/update metadata in Supabase images table
      const row = encodeImageToRow(image, storagePath, thumbnailPath);
      const { error: dbError } = await client.from('images').upsert(row, { onConflict: 'id' });

      if (dbError) {
        throw new Error(`Failed to save image metadata: ${dbError.message}`);
      }

      // Update local record with remote URLs and synced status
      await db.images.update(image.id, {
        remoteUrl: publicUrl,
        remoteThumbnailUrl: thumbnailPublicUrl,
        uploadStatus: 'synced',
      });

      uploadedCount += 1;
      maxCreatedAt = Math.max(maxCreatedAt, image.createdAt);
    } catch (error) {
      // Mark as failed and continue with next image
      await db.images.update(image.id, {
        uploadStatus: 'failed',
      });
      console.error(`Failed to upload image ${image.id}:`, error);
    }
  }

  // Update upload cursor
  if (uploadedCount > 0) {
    await setUserLastUploadedAt(userId, 'images', maxCreatedAt);
  }

  return { uploaded: uploadedCount };
}
